import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function FAQPage() {

  // Dynamically manage SEO browser titles & meta descriptions
  useEffect(() => {
  document.title = "Tesla Shipping FAQ | Answers About Tesla Transport";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Get answers to common Tesla shipping questions. Learn about transport safety, insurance coverage, shipment tracking, delivery timelines, refundable deposits, and EV shipping preparation.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'Is Tesla shipping safe, How long does Tesla shipping take, Can I track my Tesla shipment, Tesla transport insurance coverage, Tesla shipping FAQ, Tesla car shipping questions, Refundable deposit for car shipping, EV shipping preparation checklist, Tesla delivery tracking, Tesla transport safety, Tesla cargo insurance, Door-to-door Tesla shipping FAQ, Vehicle shipping frequently asked questions, AI tracking vehicle transport FAQ'
  );
}, []);

  const faqs = [
    {
      question: "How long does vehicle transport take?",
      answer: "Transit times vary based on distance and route. Generally, coast-to-coast transport takes 7-14 days, while regional moves (under 500 miles) typically take 3-7 days. We provide estimated delivery windows when you book. Exact pickup or delivery times are not guaranteed."
    },
    {
      question: "Is my Tesla safe during transport?",
      answer: "Absolutely. All our carriers are fully insured with coverage up to $150,000. We only work with verified carriers who have experience transporting electric vehicles and use soft tie-down straps to protect your Tesla's wheels and suspension."
    },
    {
      question: "What's the difference between open and enclosed transport?",
      answer: "Open transport uses multi-car carriers exposed to weather, making it more cost-effective. Enclosed transport places your vehicle in a fully covered trailer, protecting it from weather, road debris, and providing maximum security. We recommend enclosed for new or high-value Teslas."
    },
    {
      question: "How much does it cost to ship a Tesla?",
      answer: "Pricing is based on distance and transport type. Open carrier starts at $1.05/mile + $250, while enclosed is $1.68/mile + $350. A 1,000-mile open transport typically costs around $1,300. Get an instant quote on our website for exact pricing."
    },
    {
      question: "Is the deposit refundable?",
      answer: "Yes, your deposit ($199 for open, $299 for enclosed) is fully refundable until dispatch confirmation. Once a carrier has been assigned and confirmed, the deposit becomes non-refundable as resources have been committed."
    },
    {
      question: "Can I track my vehicle during transport?",
      answer: "Yes, we provide real-time updates via SMS and email throughout the transport process. You'll know when your vehicle is picked up, in transit, and delivered."
    },
    {
      question: "What if my Tesla is damaged during transport?",
      answer: "In the rare event of damage, our comprehensive insurance covers repairs up to $150,000. We conduct thorough inspections at pickup and delivery, documenting the vehicle's condition with photos."
    },
    {
      question: "Do I need to prepare my Tesla for transport?",
      answer: "Minimal preparation is needed. Ensure your Tesla has about 1/4 tank charge (or 50-100 miles range), remove personal items, disable any alarms, and note any existing damage. Our carrier will handle the rest."
    },
    {
      question: "Can you transport inoperable vehicles?",
      answer: "Currently, we only transport running vehicles. Inoperable vehicles require special equipment and handling that we don't offer at this time. Contact us for potential alternatives."
    },
    {
      question: "What areas do you service?",
      answer: "We currently service all 48 continental United States. We do not service Alaska, Hawaii, or international destinations at this time."
    },
    {
      question: "How far in advance should I book?",
      answer: "We require at least 72 hours notice for scheduling. For specific date requirements or during peak seasons (summer, end of month), we recommend booking 1-2 weeks in advance."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards for the deposit. The remaining balance is typically paid to the carrier at delivery via cash, cashier's check, or money order. Some carriers accept credit cards for an additional fee."
    },
    {
    question: "What Tesla models can ShipTesla.com transport?",
    answer: "ShipTesla.com ships all Tesla models including the Model S, Model 3, Model X, Model Y, and Cybertruck anywhere within the United States."
  },
  {
    question: "Where do you ship Tesla vehicles?",
    answer: "We ship Teslas anywhere in the USA -- all 50 states. Whether you're moving across town or coast to coast, we can arrange transport for your vehicle."
  },
  {
    question: "How much does it cost to ship a Tesla?",
    answer: "Costs depend on distance, route, and carrier type. Short routes may cost $300--$600, while cross-country shipments typically run $900--$1,500. Contact us for a free, personalized quote with no hidden fees."
  },
  {
    question: "Are there any upfront payments or hidden fees?",
    answer: "No upfront payments are required. You only pay once your vehicle is ready to be picked up. All quotes are transparent with no hidden fees."
  },
  {
    question: "Do you offer door-to-door Tesla shipping?",
    answer: "Yes. We pick up and deliver your Tesla as close to your address as legally and safely possible. In some cases such as narrow streets or HOA restrictions, a nearby meeting point may be arranged."
  },
  {
    question: "How long does Tesla shipping take?",
    answer: "Regional routes take 1--3 days, mid-range 3--7 days, and coast-to-coast shipments typically take 5--14 days depending on route and carrier availability."
  },
  {
    question: "Can I track my Tesla during shipment?",
    answer: "Yes. We provide shipment updates throughout the transport process so you're never left guessing about your vehicle's location or status."
  },
  {
    question: "How should I prepare my Tesla for shipping?",
    answer: "Charge the battery to 20--50% (not full), disable Sentry Mode, remove personal items, retract the charge port handle, and photograph the car before pickup to document its condition."
  },
  {
    question: "What is the difference between open and enclosed Tesla shipping?",
    answer: "Open carriers are economical but expose your Tesla to weather. Enclosed carriers fully protect your vehicle and are recommended for Cybertrucks, matte finishes, or brand-new Teslas. Enclosed shipping typically costs 30--50% more."
  },
  {
    question: "Is my Tesla insured during transport?",
    answer: "Yes. Your vehicle is covered by the carrier's cargo insurance throughout the entire transport process. We also recommend taking photos before and after shipping for your records."
  }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="overline mb-4">Support</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-muted-foreground">
              Everything you need to know about shipping your Tesla.
            </p>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
              >
                <AccordionTrigger className="text-left font-medium hover:no-underline py-5 text-foreground">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA */}
          <div className="mt-16 text-center">
            <p className="text-muted-foreground mb-6">
              Still have questions? We're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 border-white/20">
                  Contact Us
                </Button>
              </Link>
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black">
                  Get Your Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}


